import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, GRADIENTS, colorHelpers } from '../../utils/colors';
import PortalModal from './PortalModal';
import PortalSearchSelect from './PortalSearchSelect';
import portalDataService from '../../services/portalDataService';

const emptyItem = () => ({ product_size_id: '', quantity: '', units: 'kg', label: '' });

// BE requires exactly these lowercase unit values (§5).
const UNIT_OPTIONS = ['kg', 'meters', 'pieces'];

const productLabel = (ps) =>
  `${ps.product_name || ps.name || ps.product || 'Product'}${(ps.size_name || ps.size) ? ` — ${ps.size_name || ps.size}` : ''}`;
const locationLabel = (l) => l.delivery_location || l.name || l.location_name || l.city || `#${l.id}`;

/**
 * Create an inquiry. Customer REQUESTS — no price field; staff quote later.
 * Items require product_size_id (>0) + quantity (>0), min 1. Product and
 * location are searchable (debounced) pickers backed by the portal endpoints;
 * they fall back to manual ID entry only if the endpoints are unavailable.
 */
const PortalInquiryCreateModal = ({ isOpen, onClose, onCreated }) => {
  const { t } = useTranslation();
  const s = t('portal.inquiries', { returnObjects: true });
  const c = t('portal.common', { returnObjects: true });

  const [items, setItems] = useState([emptyItem()]);
  const [locationId, setLocationId] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [expectedDate, setExpectedDate] = useState('');
  const [remark, setRemark] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Endpoint availability — probed once when the modal opens.
  const [psUnavailable, setPsUnavailable] = useState(false);
  const [locUnavailable, setLocUnavailable] = useState(false);

  const probeAvailability = useCallback(async () => {
    const [ps, loc] = await Promise.all([
      portalDataService.getProductSizes(''),
      portalDataService.getLocations(''),
    ]);
    setPsUnavailable(!ps.success);
    setLocUnavailable(!loc.success);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setItems([emptyItem()]);
      setLocationId(''); setSelectedLocation(null); setExpectedDate(''); setRemark(''); setError('');
      probeAvailability();
    }
  }, [isOpen, probeAvailability]);

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  // Debounced fetchers passed to the search-selects.
  const fetchProducts = useCallback((term) => portalDataService.getProductSizes(term), []);
  const fetchLocations = useCallback((term) => portalDataService.getLocations(term), []);

  const handleSubmit = async () => {
    setError('');
    const cleanItems = items
      .map((it) => ({ product_size_id: Number(it.product_size_id), quantity: Number(it.quantity), units: it.units }))
      .filter((it) => Number.isInteger(it.product_size_id) && it.product_size_id > 0 && it.quantity > 0 && UNIT_OPTIONS.includes(it.units));
    if (cleanItems.length === 0) {
      setError(s?.create?.errorItems || 'Add at least one item with a product, quantity and unit.');
      return;
    }
    setSubmitting(true);
    const body = { items: cleanItems };
    if (locationId) body.location_id = Number(locationId);
    if (expectedDate) body.expected_delivery_date = expectedDate;
    if (remark.trim()) body.remark = remark.trim();

    const res = await portalDataService.createInquiry(body);
    setSubmitting(false);
    if (res.success) {
      onCreated?.(res.data);
      onClose();
    } else {
      setError(res.error || 'Failed to create inquiry.');
    }
  };

  const inputStyle = { backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text };

  const locationDetails = selectedLocation
    ? [selectedLocation.delivery_location, selectedLocation.city, selectedLocation.state, selectedLocation.zip_code].filter(Boolean).join(', ')
    : '';
  const locationContact = selectedLocation
    ? [selectedLocation.contact_person, selectedLocation.contact_phone].filter(Boolean).join(' · ')
    : '';

  return (
    <PortalModal
      isOpen={isOpen}
      onClose={onClose}
      title={s?.create?.title || 'New Inquiry'}
      maxWidth="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY, backgroundColor: 'rgba(0,0,0,0.05)' }}>
            {c?.cancel || 'Cancel'}
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting} className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60" style={{ background: GRADIENTS.PRIMARY, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
            {submitting ? (c?.saving || 'Saving…') : (s?.create?.submit || 'Submit inquiry')}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.create?.note || 'You request items; our team will quote them. No prices needed.'}</p>

        {items.map((it, idx) => (
          <div key={idx} className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{c?.product || 'Product'}</label>
              {psUnavailable ? (
                <input
                  type="number" min="1"
                  value={it.product_size_id}
                  onChange={(e) => updateItem(idx, { product_size_id: e.target.value })}
                  placeholder={s?.create?.productIdPlaceholder || 'Product size ID'}
                  className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle}
                />
              ) : (
                <PortalSearchSelect
                  fetcher={fetchProducts}
                  getOptionLabel={productLabel}
                  getOptionValue={(ps) => ps.id}
                  value={it.product_size_id}
                  onChange={(v, opt) => updateItem(idx, { product_size_id: v, label: opt ? productLabel(opt) : '' })}
                  placeholder={s?.create?.searchProduct || 'Search product…'}
                  noResultsText={s?.create?.noProducts || 'No products found'}
                  loadingText={c?.loading || 'Loading…'}
                />
              )}
            </div>
            <div className="w-24">
              <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{c?.quantity || 'Qty'}</label>
              <input type="number" min="0" step="any" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
            </div>
            <div className="w-28">
              <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{c?.unit || 'Unit'}</label>
              <select value={it.units} onChange={(e) => updateItem(idx, { units: e.target.value })} className="w-full px-2 py-2 border-2 rounded-lg text-sm" style={inputStyle}>
                {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{(s?.create?.units && s.create.units[u]) || u}</option>)}
              </select>
            </div>
            <button type="button" onClick={() => removeItem(idx)} className="px-2 py-2 text-sm rounded-lg" style={{ color: UI_COLORS.STATUS.ERROR }} title={c?.remove || 'Remove'}>×</button>
          </div>
        ))}

        <button type="button" onClick={addItem} className="text-sm font-semibold" style={{ color: UI_COLORS.BRAND.TERTIARY }}>+ {s?.create?.addItem || 'Add item'}</button>

        {psUnavailable && (
          <p className="text-xs" style={{ color: UI_COLORS.STATUS.WARNING }}>{s?.create?.productListUnavailable || 'Product list is not available for portal accounts yet — enter the product size ID manually.'}</p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.create?.expectedDelivery || 'Expected delivery (optional)'}</label>
            <input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.create?.deliveryLocation || 'Delivery location (optional)'}</label>
            {locUnavailable ? (
              <input type="number" min="1" value={locationId} onChange={(e) => setLocationId(e.target.value)} placeholder={s?.create?.locationIdPlaceholder || 'Delivery location ID'} className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
            ) : (
              <PortalSearchSelect
                fetcher={fetchLocations}
                getOptionLabel={locationLabel}
                getOptionValue={(l) => l.id}
                value={locationId}
                onChange={(v, opt) => { setLocationId(v); setSelectedLocation(opt || null); }}
                placeholder={s?.create?.searchLocation || 'Search location…'}
                noResultsText={s?.create?.noLocations || 'No locations found'}
                loadingText={c?.loading || 'Loading…'}
              />
            )}
          </div>
        </div>

        {locUnavailable && (
          <p className="text-xs" style={{ color: UI_COLORS.STATUS.WARNING }}>{s?.create?.locationListUnavailable || 'Location list unavailable — enter the delivery location ID manually.'}</p>
        )}

        {/* Selected location details */}
        {selectedLocation && (locationDetails || locationContact) && (
          <div className="rounded-lg border p-3 text-xs" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.BACKGROUND, 0.35) }}>
            <div className="font-semibold mb-0.5" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{locationLabel(selectedLocation)}</div>
            {locationDetails && <div style={{ color: UI_COLORS.TEXT.SECONDARY }}>{locationDetails}</div>}
            {locationContact && <div style={{ color: UI_COLORS.TEXT.MUTED }}>{locationContact}</div>}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{c?.remark || 'Remark'} ({c?.optional || 'optional'})</label>
          <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
        </div>

        {error && <div className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{error}</div>}
      </div>
    </PortalModal>
  );
};

export default PortalInquiryCreateModal;
